// =============================================================================
// MeuDriver — AchievementsCard
// Lista de conquistas com progresso e XP — para o dashboard do aluno
// =============================================================================

import { Link } from 'react-router-dom'
import { Trophy, ChevronRight } from 'lucide-react'
import { motion } from 'framer-motion'
import { Skeleton } from '@/components/ui/skeleton'
import { AchievementBadge } from './AchievementBadge'
import { useAchievements } from '@/hooks/useAchievements'

export function AchievementsCard() {
  const { data: achievements, isLoading } = useAchievements()

  const earned = (achievements ?? []).filter((a) => a.earned)
  const notEarned = (achievements ?? []).filter((a) => !a.earned)

  const totalXP = earned.reduce((sum, a) => sum + a.xp_reward, 0)

  return (
    <motion.section
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
      className="rounded-2xl border border-border/60 bg-white shadow-soft overflow-hidden"
    >
      {/* Header */}
      <div className="px-5 pt-5 pb-4 border-b border-border/50 flex items-center justify-between">
        <h2 className="text-base font-bold text-gray-900 flex items-center gap-2">
          <Trophy className="h-4 w-4 text-brand-600" />
          Conquistas
        </h2>
        <Link
          to="/aluno/conquistas"
          className="flex items-center gap-1 text-sm text-brand-600 font-semibold hover:text-brand-700 transition-colors"
        >
          Ver todas
          <ChevronRight className="h-3 w-3" />
        </Link>
      </div>

      <div className="px-5 py-4 space-y-4">
        {/* Resumo */}
        <div className="flex items-center gap-4 text-sm">
          <div className="flex-1 bg-brand-50 rounded-xl p-3 text-center border border-brand-100">
            <p className="text-2xl font-extrabold text-brand-700">{isLoading ? '—' : earned.length}</p>
            <p className="text-xs text-muted-foreground mt-0.5">Conquistadas</p>
          </div>
          <div className="flex-1 bg-amber-50 rounded-xl p-3 text-center border border-amber-100">
            <p className="text-2xl font-extrabold text-amber-600">{isLoading ? '—' : totalXP.toLocaleString('pt-BR')}</p>
            <p className="text-xs text-muted-foreground mt-0.5">XP acumulado</p>
          </div>
          <div className="flex-1 bg-gray-50 rounded-xl p-3 text-center border border-gray-100">
            <p className="text-2xl font-extrabold text-gray-500">{isLoading ? '—' : notEarned.length}</p>
            <p className="text-xs text-muted-foreground mt-0.5">Pendentes</p>
          </div>
        </div>

        {/* Grid de badges (prévia — 6 primeiras) */}
        {isLoading ? (
          <div className="grid grid-cols-5 sm:grid-cols-6 gap-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} className="h-20 w-full rounded-2xl" />
            ))}
          </div>
        ) : (
          <div className="flex flex-wrap gap-3 justify-start">
            {[...earned, ...notEarned].slice(0, 8).map((a) => (
              <AchievementBadge key={a.id} achievement={a} size="sm" />
            ))}
          </div>
        )}
      </div>
    </motion.section>
  )
}
